import { JSX } from 'react';

import { Step } from './step';
import { StepperContainer } from './styles';
import { StepperProps } from './types';

/**
 * A progress indicator component that displays a sequence of steps.
 *
 * @param props - The component props
 * @returns A rendered stepper component
 *
 * @example
 * ```tsx
 * <Stepper
 *   steps={[
 *     { key: 'step1', name: 'Select plan' },
 *     { key: 'step2', name: 'Create account' },
 *     { key: 'step3', name: 'Payment', hasError: true },
 *   ]}
 *   currentStep={currentStep}
 *   onStepClick={setCurrentStep}
 * />
 * ```
 */
export function Stepper({ steps, currentStep, onStepClick }: StepperProps): JSX.Element {
	const ariaLabel = steps.map(step => step.name).join(', ');

	// Convert currentStep to numeric index if it's a string key
	const currentStepIndex =
		typeof currentStep === 'string' ? steps.findIndex(step => step.key === currentStep) : currentStep;

	return (
		<StepperContainer aria-label={ariaLabel}>
			{steps.map(({ key, name, hasError }, index) => {
				return (
					<Step
						key={key}
						index={index}
						currentStep={currentStepIndex}
						stepKey={key}
						name={name}
						hasError={hasError}
						onStepClick={onStepClick}
						isLast={index === steps.length - 1}
					/>
				);
			})}
		</StepperContainer>
	);
}

export type { StepperProps, StepData } from './types';
